import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { Button, Input, INPUT_THEMES, Spin, Text, TEXT_SIZE, UI_KIT_TEXT_TYPOGRAPHY } from 'platform-components';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getStudents } from 'common.components/classes/selectors';
import { getCurrentClass } from 'common.components/user/selectors';
import { getSubjects } from 'store/courses/selectors';

import { startAddingNewStudentsList } from '../../class/actions';
import { actions } from '../actions';
import { b } from '../cn';
import { getLoadingStatus, getResultsTemplate, getSavingResultsStatus, getResultsErrorId } from '../selectors';
import type { ID_YEAR_EXAMTYPE } from '../typings';

import '../exams-results.scss';

interface Props {
    classId: number | null;
}

interface ChangeNumberField {
    min: number;
    max: number;
    id: ID_YEAR_EXAMTYPE;
    isScore?: boolean;
}

export const ExamsResultsTable = ({ classId }: Props) => {
    const dispatch = useDispatch();

    const examsResultsLoadingStatus = useSelector(getLoadingStatus);
    const examsResultsSavingStatus = useSelector(getSavingResultsStatus);
    const examsResultsTemplate = useSelector(getResultsTemplate);
    const examsResultsErrorId = useSelector(getResultsErrorId);

    const currentTeam = useSelector((storeState) => getCurrentClass(storeState, { classId }));
    const subjects = useSelector((storeState) => getSubjects(storeState, { classId }));
    const students = useSelector((storeState) => getStudents(storeState, { classId }));

    const hasGraduatingClassWithInformatics = useMemo(
        () => currentTeam && currentTeam.level > 8 && subjects.some((s) => s.subject === 'informatics'),
        [currentTeam, subjects]
    );

    useEffect(() => {
        if (classId && hasGraduatingClassWithInformatics) {
            dispatch(actions.getListExamsResultsRequest({ teamId: classId }));
        }
    }, [classId, hasGraduatingClassWithInformatics, dispatch]);

    const handleNumberFieldChange =
        ({ min, max, id, isScore }: ChangeNumberField) =>
        (e: React.ChangeEvent<HTMLInputElement>) => {
            let value: number | string = Number(e.target.value);
            if (isNaN(value)) {
                // не используем <Input type="number" />, потому что есть баг,
                // из-за которого в поле можно записать "000...", а хотелось бы один ноль
                return;
            } else if (e.target.value.length === 0) {
                value = '';
            } else if (value > max) {
                value = max;
            } else if (value < min) {
                value = min;
            } else if (e.target.value[0] === '0' && e.target.value.length > 1) {
                value = Number(e.target.value[1]);
            }

            if (isScore) {
                dispatch(actions.updateField({ id, score: value }));
            } else {
                dispatch(actions.updateField({ id, points: value }));
            }
        };

    const handleAddStudents = () => {
        dispatch(startAddingNewStudentsList());
    };

    const handleSave = () => {
        dispatch(actions.postExamsResultsRequest({ teamId: classId!, results: examsResultsTemplate }));
    };

    if (!hasGraduatingClassWithInformatics) {
        return <Text id="classes.examsResults.notGraduatingClass" size={TEXT_SIZE.XL} />;
    }

    const title = (
        <Text
            id="classes.examsResults.title"
            size={TEXT_SIZE.XL}
            values={{ level: currentTeam!.level, name: currentTeam!.name }}
        />
    );

    const isLoading = hasGraduatingClassWithInformatics && examsResultsLoadingStatus !== LOADING_STATUSES.SUCCESS;

    if (isLoading) {
        return (
            <>
                {title}
                <Spin mix={b('spin')} size={Spin.SIZE.XL} />
            </>
        );
    }

    return (
        <>
            {title}
            <div className={b('table')}>
                <div className={b('table-head')}>
                    <div className={b('table-row', { 'custom-cols': true })}>
                        <div className={b('table-cell')} />
                        <div className={b('table-cell', { center: true })}>2022</div>
                        <div className={b('table-cell', { center: true })}>2023</div>
                    </div>
                    <div className={b('table-row')}>
                        <div className={b('table-cell')}>
                            <Text
                                id="classes.examsResults.table.student"
                                typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphLMedium}
                            />
                        </div>
                        <div className={b('table-cell')}>
                            <Text
                                id="classes.examsResults.table.points"
                                typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphLMedium}
                                values={{ min: 0, max: 19 }}
                            />
                        </div>
                        <div className={b('table-cell')}>
                            <Text
                                id="classes.examsResults.table.score"
                                typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphLMedium}
                                values={{ min: 2, max: 5 }}
                            />
                        </div>
                        <div className={b('table-cell')}>
                            <Text
                                id="classes.examsResults.table.points"
                                typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphLMedium}
                                values={{ min: 0, max: 19 }}
                            />
                        </div>
                        <div className={b('table-cell')}>
                            <Text
                                id="classes.examsResults.table.score"
                                typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphLMedium}
                                values={{ min: 2, max: 5 }}
                            />
                        </div>
                    </div>
                    <div className={b('table-row')} />
                </div>

                <div className={b('table-body')}>
                    {/* eslint-disable-next-line complexity */}
                    {students?.map((student) => {
                        const id2022 = `${student.id}_2022_OGE`;
                        const id2023 = `${student.id}_2023_OGE`;

                        const lock2022 =
                            examsResultsTemplate[id2023] &&
                            (examsResultsTemplate[id2023].points !== '' || examsResultsTemplate[id2023].score !== '');
                        const lock2023 =
                            examsResultsTemplate[id2022] &&
                            (examsResultsTemplate[id2022].points !== '' || examsResultsTemplate[id2022].score !== '');

                        return (
                            <div className={b('table-row')} key={student.id}>
                                <div className={b('table-cell', { 'vertical-center': true })}>{`${
                                    student.last_name || ''
                                } ${student.first_name || ''}`}</div>
                                <div className={b('table-cell')}>
                                    <Input
                                        disabled={lock2022}
                                        onChange={handleNumberFieldChange({ min: 0, max: 19, id: id2022 })}
                                        theme={INPUT_THEMES.YANDEX}
                                        value={examsResultsTemplate[id2022]?.points ?? ''}
                                    />
                                </div>
                                <div className={b('table-cell')}>
                                    <Input
                                        disabled={lock2022}
                                        onChange={handleNumberFieldChange({
                                            min: 2,
                                            max: 5,
                                            id: id2022,
                                            isScore: true,
                                        })}
                                        theme={INPUT_THEMES.YANDEX}
                                        value={examsResultsTemplate[id2022]?.score ?? ''}
                                    />
                                </div>

                                <div className={b('table-cell')}>
                                    <Input
                                        disabled={lock2023}
                                        onChange={handleNumberFieldChange({ min: 0, max: 19, id: id2023 })}
                                        theme={INPUT_THEMES.YANDEX}
                                        value={examsResultsTemplate[id2023]?.points ?? ''}
                                    />
                                </div>
                                <div className={b('table-cell')}>
                                    <Input
                                        disabled={lock2023}
                                        onChange={handleNumberFieldChange({
                                            min: 2,
                                            max: 5,
                                            id: id2023,
                                            isScore: true,
                                        })}
                                        theme={INPUT_THEMES.YANDEX}
                                        value={examsResultsTemplate[id2023]?.score ?? ''}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {examsResultsErrorId && <Text className={b('error')} id={examsResultsErrorId} />}
            <div className={b('controls')}>
                <Button onClick={handleAddStudents} rounded size={Button.SIZE.L} view={Button.VIEW.SECONDARY}>
                    <Text
                        id="classes.examsResults.addStudentsButton"
                        typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphXLMedium}
                    />
                </Button>

                <Button
                    className={b('save-btn')}
                    onClick={handleSave}
                    progress={examsResultsSavingStatus === LOADING_STATUSES.LOADING}
                    rounded
                    size={Button.SIZE.L}
                    view={Button.VIEW.ACTION}
                >
                    <Text id="classes.examsResults.saveButton" typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphXLMedium} />
                </Button>
            </div>
        </>
    );
};
